import React, { useMemo, useState } from 'react';
import { ArrowDownTrayIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Card from '../../ui/Card';
import { AnalyticsSnapshot, BillableBucket, SnapshotDay } from '../../../types/analytics';
import { BUCKET_COLOR, BUCKET_HELP, BUCKET_LABEL, downloadCsv, fmtHours, fmtMoney, fmtPct, periodLabel } from './format';

const BILLABLE: BillableBucket[] = ['completed', 'held', 'upcoming']; // stack order, bottom -> top

/* ----------------------------------------------------------------------- */
/* Stat tile                                                                */
/* ----------------------------------------------------------------------- */

interface TileProps {
  label: string;
  value: string;
  sub?: React.ReactNode;
  swatch?: string;
  testId?: string;
}

const Tile: React.FC<TileProps> = ({ label, value, sub, swatch, testId }) => (
  <Card className="p-4">
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-clinic-muted flex items-center gap-1.5">
        {swatch && <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: swatch }} aria-hidden="true" />}
        {label}
      </p>
      <p className="text-2xl font-semibold text-clinic-dark mt-1">{value}</p>
      {sub && <p className="text-xs text-clinic-muted mt-1">{sub}</p>}
    </div>
  </Card>
);

const Delta: React.FC<{ now: number; before: number; label: string; fmt: (n: number) => string }> = ({ now, before, label, fmt }) => {
  if (!before) return <>No {label} data to compare</>;
  const diff = now - before;
  const pct = Math.round((diff / before) * 100);
  return <>{diff >= 0 ? '▲' : '▼'} {Math.abs(pct)}% vs {label} ({fmt(before)})</>;
};

/* ----------------------------------------------------------------------- */
/* Daily stacked columns                                                    */
/* ----------------------------------------------------------------------- */

const niceMax = (v: number): number => {
  if (v <= 4) return 4;
  const step = v <= 12 ? 4 : v <= 40 ? 10 : 20;
  return Math.ceil(v / step) * step;
};

const DailyChart: React.FC<{ days: SnapshotDay[]; todayIso: string }> = ({ days, todayIso }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(1, ...days.map(d => d.completed + d.upcoming + d.held)));
  const H = 168;
  const ticks = [max, max / 2, 0];

  return (
    <div>
      <div className="flex items-center gap-4 flex-wrap mb-3 text-xs text-clinic-dark" aria-label="Legend">
        {BILLABLE.map(k => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: BUCKET_COLOR[k] }} aria-hidden="true" />
            {BUCKET_LABEL[k]}
          </span>
        ))}
      </div>
      <div className="flex">
        <div className="flex flex-col justify-between text-[10px] text-clinic-muted pr-2 text-right" style={{ height: H }}>
          {ticks.map(t => <span key={t}>{t} h</span>)}
        </div>
        <div className="relative flex-1">
          {/* gridlines */}
          {ticks.map((t, i) => (
            <div key={t} className="absolute left-0 right-0 border-t border-clinic-primary-lighter" style={{ top: (i / (ticks.length - 1)) * H }} />
          ))}
          <div className="relative flex items-end" style={{ height: H }} role="img"
               aria-label="Booked hours per day, split into completed, held and upcoming. Use the table view for exact values.">
            {days.map((d, i) => {
              const total = d.completed + d.upcoming + d.held;
              const segs = BILLABLE.filter(k => d[k] > 0);
              return (
                <div
                  key={d.date}
                  className="relative flex-1 h-full flex items-end justify-center cursor-default"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(h => (h === i ? null : h))}
                  data-testid={`day-${d.date}`}
                >
                  {hover === i && <div className="absolute inset-0 bg-clinic-primary/5" />}
                  <div className="relative flex flex-col-reverse items-stretch" style={{ width: '70%', maxWidth: 18 }}>
                    {segs.map((k, si) => (
                      <div
                        key={k}
                        style={{
                          height: Math.max(2, (d[k] / max) * H - (si > 0 ? 2 : 0)),
                          marginBottom: si > 0 ? 2 : 0,                 // 2px surface gap between segments
                          background: BUCKET_COLOR[k],
                          borderTopLeftRadius: si === segs.length - 1 ? 4 : 0,
                          borderTopRightRadius: si === segs.length - 1 ? 4 : 0,
                        }}
                      />
                    ))}
                  </div>
                  {hover === i && (
                    <div className="absolute z-10 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white border border-clinic-primary-lighter shadow-clinic-md rounded-lg px-3 py-2 text-xs text-clinic-dark pointer-events-none">
                      <p className="font-medium mb-1">
                        {new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        {d.date === todayIso ? ' · today' : ''}
                      </p>
                      {total === 0 ? <p className="text-clinic-muted">No bookings</p> : BILLABLE.slice().reverse().filter(k => d[k] > 0).map(k => (
                        <p key={k} className="flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: BUCKET_COLOR[k] }} />
                          {BUCKET_LABEL[k]}: <strong>{fmtHours(d[k])}</strong>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex text-[10px] text-clinic-muted mt-1">
            {days.map((d, i) => {
              const day = Number(d.date.slice(8));
              const isToday = d.date === todayIso;
              return (
                <span key={d.date} className={`flex-1 text-center ${isToday ? 'text-clinic-dark font-semibold' : ''}`}>
                  {isToday ? 'today' : (day === 1 || day % 5 === 0) && !days[i + 1]?.date.endsWith(todayIso.slice(8)) ? day : ''}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Snapshot tab                                                             */
/* ----------------------------------------------------------------------- */

const SnapshotTab: React.FC<{ snapshot: AnalyticsSnapshot }> = ({ snapshot: s }) => {
  const [showDailyTable, setShowDailyTable] = useState(false);
  const todayIso = useMemo(() => {
    const d = new Date(s.as_of);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [s.as_of]);

  const realizedHours = s.totals.completed.hours + s.totals.held.hours;
  const totalRevenue = s.revenue.realized + s.revenue.scheduled;
  const mixTotal = s.mix_hours.client + s.mix_hours.self + s.mix_hours.pod;
  const prevLabel = periodLabel(s.previous.period).split(' ')[0];

  const exportCsv = () => downloadCsv(`clinic-studios-snapshot-${s.period}.csv`, [
    [`Clinic Studios snapshot for ${periodLabel(s.period)}`, `as of ${new Date(s.as_of).toLocaleString()}`],
    ['Clinician', 'Tier', 'Completed h', 'Upcoming h', 'Held h', 'Released h', 'Realized $ (projected)', 'Scheduled $ (projected)', 'Late cancellations'],
    ...s.clinicians.map(c => [c.name, c.tier, c.completed, c.upcoming, c.held, c.released,
      Math.round(c.revenue_realized * 100) / 100, Math.round(c.revenue_scheduled * 100) / 100, c.late_cancellations]),
  ]);

  return (
    <div className="space-y-6">
      {/* What the words mean — the whole point of this page */}
      <div className="flex items-start gap-3 p-4 rounded-card border border-clinic-primary-lighter bg-white">
        <InformationCircleIcon className="h-5 w-5 text-clinic-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-clinic-dark">
          <strong>Completed</strong> hours have already happened. <strong>Upcoming</strong> hours are only scheduled and can
          still shrink, move or cancel, so they are never counted as earned. <strong>Held</strong> hours were cancelled inside the{' '}
          {s.cancellation_window_hours}-hour window and stay billed.
          {s.period_is_closed ? ' This month is closed, so everything left is final.' : ''}
        </p>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile testId="tile-completed" swatch={BUCKET_COLOR.completed} label="Completed" value={fmtHours(s.totals.completed.hours)}
              sub={`${s.totals.completed.count} bookings · already happened`} />
        <Tile testId="tile-upcoming" swatch={BUCKET_COLOR.upcoming} label="Upcoming" value={fmtHours(s.totals.upcoming.hours)}
              sub={`${s.totals.upcoming.count} bookings · scheduled, can change`} />
        <Tile testId="tile-held" swatch={BUCKET_COLOR.held} label="Held" value={fmtHours(s.totals.held.hours)}
              sub={`${s.totals.held.count} late cancels · still billed`} />
        <Tile label="Released" value={fmtHours(s.totals.released.hours)}
              sub={`${s.totals.released.count} cancelled · never billed`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile testId="tile-revenue-realized" label="Room revenue, realized" value={fmtMoney(s.revenue.realized)}
              sub={`${fmtHours(realizedHours)} completed + held`} />
        <Tile testId="tile-revenue-scheduled" label="Room revenue, scheduled" value={fmtMoney(s.revenue.scheduled)}
              sub="Not earned yet. Can still change." />
        <Tile label="Month projection" value={fmtMoney(totalRevenue)}
              sub={<Delta now={totalRevenue} before={s.previous.revenue} label={prevLabel} fmt={fmtMoney} />} />
        <Tile label="Membership fees" value={fmtMoney(s.revenue.membership)}
              sub={`${s.revenue.active_members} members × ${fmtMoney(s.revenue.membership_fee)}`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Studio occupancy" value={fmtPct(s.occupancy.utilization)}
              sub={`${fmtHours(s.occupancy.studio_billable_hours)} of ${fmtHours(s.occupancy.studio_capacity_hours)} open`} />
        <Tile label="Booked hours" value={fmtHours(s.billable_hours)}
              sub={<Delta now={s.billable_hours} before={s.previous.billable_hours} label={prevLabel} fmt={fmtHours} />} />
        <Tile label="Active clinicians" value={String(s.activity.active_clinicians)}
              sub={`${s.activity.active_clients} clients · ${s.activity.bookings} bookings`} />
        <Tile label="Cancellation rate" value={fmtPct(s.cancellations.rate)}
              sub={`${s.cancellations.late} late of ${s.cancellations.cancelled} · check-in ${fmtPct(s.activity.check_in_rate)}`} />
      </div>
      <p className="text-xs text-clinic-muted -mt-3">{s.revenue.note}</p>

      {/* Daily */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-clinic-dark">Booked hours by day</h2>
          <button type="button" onClick={() => setShowDailyTable(v => !v)} className="text-xs text-clinic-primary hover:text-clinic-primary-dark">
            {showDailyTable ? 'Show chart' : 'View as table'}
          </button>
        </div>
        {showDailyTable ? (
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-clinic-muted">
                <tr><th className="py-1 pr-4">Date</th><th className="py-1 pr-4">Completed</th><th className="py-1 pr-4">Held</th><th className="py-1">Upcoming</th></tr>
              </thead>
              <tbody>
                {s.daily.filter(d => d.completed + d.held + d.upcoming > 0).map(d => (
                  <tr key={d.date} className="border-t border-clinic-primary-lighter">
                    <td className="py-1 pr-4">{d.date}</td><td className="py-1 pr-4">{fmtHours(d.completed)}</td>
                    <td className="py-1 pr-4">{fmtHours(d.held)}</td><td className="py-1">{fmtHours(d.upcoming)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <DailyChart days={s.daily} todayIso={todayIso} />}
      </Card>

      {/* Clinicians */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-clinic-dark">By clinician</h2>
          <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1 text-xs text-clinic-primary hover:text-clinic-primary-dark">
            <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
          </button>
        </div>
        {s.clinicians.length === 0 ? (
          <p className="text-sm text-clinic-muted">No bookings in {periodLabel(s.period)}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-clinic-muted">
                <tr>
                  <th className="py-2 pr-4">Clinician</th><th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4" title={BUCKET_HELP.completed}>Completed</th>
                  <th className="py-2 pr-4" title={BUCKET_HELP.upcoming}>Upcoming</th>
                  <th className="py-2 pr-4" title={BUCKET_HELP.held}>Held</th>
                  <th className="py-2 pr-4" title={BUCKET_HELP.released}>Released</th>
                  <th className="py-2 pr-4">Realized</th><th className="py-2 pr-4">Scheduled</th><th className="py-2">Late cancels</th>
                </tr>
              </thead>
              <tbody>
                {s.clinicians.map(c => (
                  <tr key={c.clinician_id} className="border-t border-clinic-primary-lighter">
                    <td className="py-2 pr-4 font-medium text-clinic-dark">{c.name}</td>
                    <td className="py-2 pr-4 capitalize text-clinic-muted">{c.tier ?? 'n/a'}</td>
                    <td className="py-2 pr-4">{fmtHours(c.completed)}</td>
                    <td className="py-2 pr-4">{fmtHours(c.upcoming)}</td>
                    <td className="py-2 pr-4">{fmtHours(c.held)}</td>
                    <td className="py-2 pr-4 text-clinic-muted">{fmtHours(c.released)}</td>
                    <td className="py-2 pr-4">{fmtMoney(c.revenue_realized)}</td>
                    <td className="py-2 pr-4 text-clinic-muted">{fmtMoney(c.revenue_scheduled)}</td>
                    <td className="py-2">{c.late_cancellations || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Rooms */}
        <Card className="p-5">
          <h2 className="text-base font-semibold text-clinic-dark mb-3">Room utilization</h2>
          <div className="space-y-3">
            {s.rooms.map(r => (
              <div key={r.room_id}>
                <div className="flex justify-between text-sm">
                  <span className="text-clinic-dark">{r.name}{r.room_type === 'pod' ? ' (pod)' : ''}</span>
                  <span className="text-clinic-muted">{fmtPct(r.utilization)} · {fmtHours(r.billable_hours)}</span>
                </div>
                <div className="mt-1 h-2.5 rounded-full bg-clinic-surface-sunken overflow-hidden flex" title={`${fmtHours(r.billable_hours)} of ${fmtHours(r.open_hours)} open hours`}>
                  {(['completed', 'held', 'upcoming'] as BillableBucket[]).map(k => r[k] > 0 && r.open_hours > 0 ? (
                    <div key={k} style={{ width: `${Math.min(100, (r[k] / r.open_hours) * 100)}%`, background: BUCKET_COLOR[k], marginRight: 2 }} />
                  ) : null)}
                </div>
              </div>
            ))}
            {s.rooms.length === 0 && <p className="text-sm text-clinic-muted">No rooms configured.</p>}
          </div>
          <p className="text-xs text-clinic-muted mt-3">Share of clinic open hours this month ({fmtHours(s.occupancy.open_hours_per_room)} per room).</p>
        </Card>

        {/* Mix */}
        <Card className="p-5">
          <h2 className="text-base font-semibold text-clinic-dark mb-3">Who the hours are for</h2>
          <dl className="grid grid-cols-3 gap-3 text-center">
            {([['client', 'Client sessions'], ['self', 'Clinician own use'], ['pod', 'Pods']] as const).map(([k, label]) => (
              <div key={k} className="rounded-lg bg-clinic-surface p-3">
                <dt className="text-xs text-clinic-muted">{label}</dt>
                <dd className="text-lg font-semibold text-clinic-dark">{fmtHours(s.mix_hours[k])}</dd>
                <dd className="text-xs text-clinic-muted">{mixTotal ? fmtPct(s.mix_hours[k] / mixTotal) : 'n/a'}</dd>
              </div>
            ))}
          </dl>
          <p className="text-xs text-clinic-muted mt-3">
            {s.totals.pending.count > 0
              ? `${s.totals.pending.count} booking request${s.totals.pending.count === 1 ? '' : 's'} (${fmtHours(s.totals.pending.hours)}) waiting for approval are not included.`
              : 'Booked (completed, held and upcoming) hours only.'}
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SnapshotTab;
